<template>
  <div>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add application to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <default-confirmation
        text-delete="Delete this apps completely?"
        v-else-if="event === 'delete'"
        @close="event = null"
        @delete="deleteAction"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        entity-type="APPLICATION"
        type="APPLICATION"
        :callback-after="load"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :selected-out="eValue"
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      @event="eventHandler"
    />
  </div>
</template>

<script>
import pageActions from './pageActions';
import selectedActions from './selectedActions';
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  reactive,
  toRefs
} from '@vue/composition-api';
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import useApps from '@/api/apps';
import useAppsV2 from '@/services/apps/useApps';
import useSettings from '@/api/tableSettings';
import useMedia from '@/compositions/useMedia';
import toSnakeCase from 'lodash.snakecase';
import cloneDeep from 'lodash.clonedeep';
import { nanoid } from 'nanoid';
import useSchemas from '@/api/schemas';
import useAuth from '@/api/auth';
import useCommonModal from '@/compositions/useCommonModal';
import { RELOAD_INTERVAL_SEC } from "@/utils/constants";
export default {
  name: 'apps',
  metaInfo: {
    titleTemplate: '%s | Applications'
  },
  components: {
    DefaultConfirmation,
    addNew: () => import('@/components/app-entity/modals/CreateObject'),
    changeSchema: () => import('@/components/app-entity/modals/EditScheme')
  },
  setup(props, { root }) {
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { apps } = useApps();
    const { load: loadList, params, setParams } = apps;
    const appsComposition = useAppsV2();
    const { computeIcon } = useMedia();
    const modalComposition = useCommonModal();
    const { schemas: schemasApi } = new useSchemas();

    const { profile } = useAuth();
    let timerId = null;
    const eValue = ref([]);
    const schemasList = ref([]);
    const visibleColumns = ref([]);
    const allColumns = ref([
      'name',
      'favourite',
      'muted',
      'online',
      'enabled',
      'drivername',
      'editorgroup',
      'usergroup',
      'readergroup'
    ]);

    const showOnMobile = ['favourite', 'enabled'];

    function deleteAction() {
      apps.remove(eValue.value.map(i => ({ id: i.id }))).then(() => {
        load();
        event.value = null;
        eValue.value = [];
      });
    }

    function load(filterOuter) {
      let filter = {
        ...appsComposition.variables.value.filter,
        ...params.value.filter,
        ...filterOuter
      };

      if (root.$route.query.filterTable !== undefined) {
        filter.name = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      appsComposition.variables.value.filter = {
        ...filter,
        ...filterOuter,
        schemaType: { equalTo: 'application' }
      };

      return appsComposition.restart();
    }

    const event = ref(null);
    const bufferSelected = ref([]);
    const toolbar = ref({
      title: 'Applications',
      showAccess: true,
      pageActions,
      selectedActions
    });

    const settings = computed(() => {
      return visibleColumns.value;
    });

    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`object.${item}`),
        value: item,
        showOnMobile: showOnMobile.includes(item),
        sortable: item !== 'online'
      }));
    });

    const items = computed(() => {
      if (appsComposition.items.value) {
        return appsComposition.items.value.map(i => ({
          ...i,
          online: i.online,
          iconFallback: 'mdi-application-cog',
          drivername: i.schemaName,
          avatar: computeIcon(i.schema)
        }));
      } else {
        return [];
      }
    });

    const state = reactive({
      table: {
        filters: [],
        total: appsComposition.totalCount,
        items,
        columns,
        settings,
        loading: appsComposition.loading,
        itemRoute: 'appsTableComponent'
      }
    });

    function eventHandler({ event: e, data }) {
      eValue.value = data;
      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('appsSettings', data);
          break;
        }
        case 'delete': {
          event.value = 'delete';
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'changeSchema': {
          bufferSelected.value = data;
          event.value = 'changeSchema';
          break;
        }
        case 'massiveDisable': {
          appsComposition.update({
            input: {
              mnPatch: eValue.value.map(i => ({ id: i.id, enabled: false }))
            }
          });
          break;
        }
        case 'massiveAccessRights': {
          modalComposition.modal.show({
            component: () =>
              import('@/components/app-entity/modals/AppDialog.vue'),
            bind: {
              nameModal: 'EditRightsGroup'
            },
            slots: {
              default: {
                component: () =>
                  import('@/components/app-entity/modals/EditRightsGroup'),
                bind: {
                  entity: null
                },
                on: {
                  close() {
                    modalComposition.modal.hide('EditRightsGroup');
                  },
                  save(e) {
                    const payload = {};

                    if (e.editorgroup) {
                      payload.editorgroup = e.editorgroup;
                    }
                    if (e.readergroup) {
                      payload.readergroup = e.readergroup;
                    }
                    if (e.usergroup) {
                      payload.usergroup = e.usergroup;
                    }

                    appsComposition
                      .update({
                        input: {
                          mnPatch: eValue.value.map(i => ({
                            id: i.id,
                            ...payload
                          }))
                        }
                      })
                      .then(() => {
                        load();
                        eValue.value = [];
                        event.value = null;
                      });
                    modalComposition.modal.hide('EditRightsGroup');
                  }
                }
              }
            }
          });
          break;
        }
        case 'massiveEnable': {
          appsComposition.update({
            input: {
              mnPatch: eValue.value.map(i => ({ id: i.id, enabled: true }))
            }
          });
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);

          let filter = {
            schemaType: { equalTo: 'application' }
          };

          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });
          appsComposition.variables.value.filter = cloneDeep(filter);
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              appsComposition.variables.value.sort = null;
              appsComposition.variables.value.orderBy = 'NATURAL';
            }
          }

          if (data.desc && params.value.sort) {
            if (params.value.sort === 'drivername') {
              result.orderBy = `SCHEMA_NAME_${data.desc[0] ? 'DESC' : 'ASC'}`;
            } else {
              const orderBy = toSnakeCase(params.value.sort).toUpperCase();
            result.orderBy = `${orderBy}_${data.desc[0] ? 'DESC' : 'ASC'}`;
            }
          }

          appsComposition.variables.value.first = result.first;
          appsComposition.variables.value.offset = result.offset;
          appsComposition.variables.value.orderBy =
            result.orderBy || 'NAME_ASC';
          load();
          break;
        }
      }
    }
    function saveHandler(data) {
      switch (event.value) {
        case 'addNew': {
          apps
            .save({
              object: {
                schemaId: data.schema,
                name: data.name,
                enabled: data.enabled,
                description: data.description,
                editorgroup: data.editorgroup
              }
            })
            .then(r => {
              eValue.value = [];
              event.value = null;
              root.$router.push({
                name: 'appsTableComponent',
                params: { id: r.data.createObject.object.id }
              });
              load();
            });
          break;
        }
        case 'changeSchema': {
          apps
            .update(
              bufferSelected.value.map(item => ({
                id: item.id,
                schemaId: data
              }))
            )
            .then(r => {
              event.value = null;
              eValue.value = [];
              load();
            });
          break;
        }
      }
    }

    watch(
      () => root.$route.query.filterTable,
      () => {
        load();
      }
    );

    onUnmounted(() => {
      clearInterval(timerId);
      setParams(null);
    });

    onMounted(async () => {
      appsComposition.variables.value.orderBy = 'NAME_ASC';
      appsComposition.variables.value.first =
        profile.value?.objectProperties.find(
          item => item.key === 'programNumbersOfRows'
        )?.value || 20;

      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('appsSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await schemasApi
        .loadPureSchemata('APPLICATION', {
          parentSchemaExists: false
        })
        .then(data => {
          schemasList.value = data.schemata.map(schema => ({
            id: schema.id,
            value: schema.id,
            name: schema.name
          }));
        });

      appsComposition.load();
      await root.$nextTick();

      const filters = [
        {
          id: 'favourite',
          title: 'Favorite',
          getFilter() {
            return {
              favourite: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            {
              value: true,
              name: 'Yes',
              id: nanoid()
            },
            {
              name: 'No',
              value: false,
              id: nanoid()
            }
          ]
        },
        {
          title: 'Muted',
          id: 'muted',
          type: 'tags',
          getFilter() {
            return {
              muted: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Yes', id: nanoid(), value: true },
            { name: 'No', id: nanoid(), value: false }
          ]
        },
        {
          id: 'enabled',
          title: 'Status',
          type: 'tags',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          getFilter() {
            return {
              schemaName: {
                in: this.model.map(item => item.name)
              }
            };
          },
          id: 'Schemas name',
          model: [],
          type: 'tags',
          values: schemasList.value,
          title: 'Schema name'
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    });
    return {
      ...toRefs(state),
      eValue,
      event,
      toolbar,
      eventHandler,
      saveHandler,
      deleteAction,
      load
    };
  }
};
</script>
